import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { vocabularyApi } from '@/api/vocabulary'
import { Button } from '@/components/ui/Button'
import { CefrBadge } from '@/components/CefrBadge'
import { copy } from '@/lib/copy'
import { isSenseLearned } from '@/lib/senseUtils'
import type { LocalizedSense, UserWordDetail } from '@/types/user'
import type { CefrLevel } from '@/types/common'

interface LearnAnotherMeaningProps {
  word: UserWordDetail
}

export function LearnAnotherMeaningPanel({ word }: LearnAnotherMeaningProps) {
  const queryClient = useQueryClient()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const senses = word.localizedSenses ?? []
  const learnedFromCard = (word.knownSenses ?? []).map((s) => ({
    senseKey: s.senseKey,
    translation: s.translation,
  }))

  const availableSenses = senses.filter(
    (sense) => !isSenseLearned(sense.senseKey, sense.translation, learnedFromCard),
  )

  const addMutation = useMutation({
    mutationFn: (sense: LocalizedSense) =>
      vocabularyApi.addWord({
        word: word.word,
        translation: sense.translation,
        senseKey: sense.senseKey,
        source: word.source,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vocabulary'] })
      queryClient.invalidateQueries({ queryKey: ['vocabulary', word.id] })
      queryClient.invalidateQueries({ queryKey: ['vocabularyStats'] })
      setError(null)
      setPickerOpen(false)
    },
    onError: (err: unknown) => {
      const response = (err as { response?: { status: number; data?: { message?: string } } })?.response
      if (response?.status === 409) {
        setError(copy.word.senseAlreadyLearned)
        return
      }
      if (response?.status === 400 && response.data?.message) {
        setError(response.data.message)
        return
      }
      setError(copy.vocabulary.addWordFailed)
    },
  })

  if (senses.length <= 1 && availableSenses.length === 0) {
    return null
  }

  return (
    <div className="mb-6 rounded-xl border bg-card p-4" data-testid="learn-another-meaning">
      {availableSenses.length > 0 && (
        <>
          {!pickerOpen ? (
            <Button
              type="button"
              className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/80"
              onClick={() => {
                setError(null)
                setPickerOpen(true)
              }}
              data-testid="learn-another-meaning-btn"
            >
              {copy.word.learnAnotherMeaning}
            </Button>
          ) : (
            <div className="space-y-3" data-testid="sense-picker">
              <p className="text-sm font-medium text-foreground">{copy.word.sensePickerTitle}</p>
              <div className="space-y-2">
                {availableSenses.map((sense) => (
                  <label
                    key={sense.senseKey}
                    className="flex cursor-pointer items-start gap-2 rounded-lg border border-border px-3 py-2 hover:bg-muted/30"
                  >
                    <input
                      type="radio"
                      name="sense-picker"
                      className="mt-1"
                      onChange={() => addMutation.mutate(sense)}
                      disabled={addMutation.isPending}
                    />
                    <span className="text-sm">
                      {sense.partOfSpeech && (
                        <span className="mr-1.5 text-xs italic text-muted-foreground">
                          {sense.partOfSpeech}
                        </span>
                      )}
                      {sense.cefrLevel && (
                        <CefrBadge level={sense.cefrLevel as CefrLevel} />
                      )}
                      <span className="ml-1 font-medium">{sense.translation}</span>
                      {sense.nativeGloss && sense.nativeGloss !== sense.translation && (
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                          {sense.nativeGloss}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
              {error && (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}
              <Button
                type="button"
                className="bg-secondary text-secondary-foreground hover:bg-secondary/80"
                onClick={() => setPickerOpen(false)}
              >
                Cancel
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
